import React from "react";
import { useEffect, useState } from "react";
import {
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Paper,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	Typography,
} from "@mui/material";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { clientService, IListagemCliente } from "../../../../shared/services/api/client/clientService";
import { ToastContent } from "../../../../shared/components";
import { returnMessageError } from "../../../../shared/utils/returnMessageError";

interface PartnersCardProps {
	id: string;
	canActivatePartners?: boolean;
}

export const PartnersCard: React.FC<PartnersCardProps> = ({ id, canActivatePartners = false }) => {
	const [partners, setPartners] = useState<IListagemCliente[]>([]);
	const [Isloading, setIsLoading] = useState<boolean>(true);
	const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
	const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
	const [pendingPartner, setPendingPartner] = useState<IListagemCliente | null>(null);
	const { debounce } = useDebounce();
	const toastHandler = useToast();

	type TStatus = "active" | "inactive";
	const normalizeStatus = (raw: unknown): TStatus => {
		const value = String(raw ?? "").toLowerCase();
		if (value === "inactive" || value === "0" || value.includes("inativ")) return "inactive";
		return "active";
	};

	const statusChipSx = (status: TStatus) => {
		if (status === "inactive") {
			return {
				color: "#e40909",
				fontWeight: 500,
				background: "#fce4e4",
				border: "0.5px solid #f1f1f1",
			} as const;
		}

		return {
			color: "#0f8a0d",
			fontWeight: 500,
			background: "#d9f8e5",
			border: "0.5px solid #f1f1f1",
		} as const;
	};

	const getRelationIdFromPartner = (partner: IListagemCliente): string | undefined => {
		const anyPartner = partner as any;
		return (
			partner?.id ||
			anyPartner?.id ||
			anyPartner?.relationId ||
			anyPartner?.clientRelationId ||
			anyPartner?.client_relation_id ||
			anyPartner?.clientRelation?.id ||
			anyPartner?.relation?.id ||
			anyPartner?.partnerId
		);
	};

	const showToastError = (titleMessage: string, contentMessage: string) => {
		toastHandler.present({
			type: "error",
			position: "top-right",
			messageOrContent: <ToastContent title={titleMessage} content={contentMessage} />,
		});
	};

	const fetchPartnersByClient = async () => {
		try {
			const result = await clientService.getPartners(id);
			if (result instanceof Error) {
				showToastError("Alerta!!", "Erro: " + result?.message);
				return;
			}
			setPartners(result.data);
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			showToastError(titleMessage, contentMessage);
		}
	};

	const getPartnersByClient = (useDebounceCall: boolean = true) => {
		if (id !== "novo") {
			setIsLoading(true);
			const run = async () => {
				await fetchPartnersByClient();
				setIsLoading(false);
			};

			if (useDebounceCall) {
				debounce(run);
			} else {
				run();
			}
		}
	};

	const setPartnerStatus = async (partner: IListagemCliente, status: TStatus) => {
		const relationId = getRelationIdFromPartner(partner);
		if (!relationId) {
			showToastError("Alerta!!", "Não foi possível identificar o ID da relação para atualizar o status.");
			return;
		}

		setIsUpdatingStatus(true);
		try {
			const result = await clientService.updateClientRelationStatus(relationId, status);
			if (result instanceof Error) {
				showToastError("Alerta!!", "Erro: " + result.message);
				return;
			}
			getPartnersByClient(false);
		} catch (err: any) {
			const { titleMessage, contentMessage } = returnMessageError(err);
			showToastError(titleMessage, contentMessage);
		} finally {
			setIsUpdatingStatus(false);
		}
	};

	const deactivateOtherPartners = async (partnerToKeepActive: IListagemCliente) => {
		const otherActives = partners.filter(
			(p) => normalizeStatus((p as any).status) === "active" && p.id !== partnerToKeepActive.id,
		);

		for (const otherPartner of otherActives) {
			const relationId = getRelationIdFromPartner(otherPartner);
			if (!relationId) {
				showToastError("Alerta!!", "Não foi possível identificar o ID de uma das relações para desativar os demais parceiros.");
				throw new Error("Missing relationId for partner");
			}

			const result = await clientService.updateClientRelationStatus(relationId, "inactive");
			if (result instanceof Error) {
				showToastError("Alerta!!", "Erro: " + result.message);
				throw result;
			}
		}
	};

	const handleChangeActive = (partner: IListagemCliente) => {
		if (!canActivatePartners) return;
		const isActive = normalizeStatus((partner as any).status) === "active";
		if (isActive) {
			setPartnerStatus(partner, "inactive");
			return;
		}

		const hasAnotherActive = partners.some((p) => normalizeStatus((p as any).status) === "active" && p.id !== partner.id);
		if (hasAnotherActive) {
			setPendingPartner(partner);
			setIsConfirmDialogOpen(true);
			return;
		}

		setPartnerStatus(partner, "active");
	};

	useEffect(() => {
		getPartnersByClient();
	}, [id]);

	return (
		<TableContainer
			component={Paper}
			variant="outlined"
			sx={{
				marginLeft: 1,
				marginTop: 1,
				marginRight: 2,
				width: "auto",
				padding: 4,
				overflow: "auto",
			}}
			aria-label="customized table"
		>
			<Table aria-label="a dense table">
				<TableRow>
					<TableCell sx={{ color: "#7e7777b5" }}> Nome</TableCell>
					<TableCell sx={{ color: "#7e7777b5" }}> E-mail</TableCell>
					<TableCell sx={{ color: "#7e7777b5" }}> Status</TableCell>
					<TableCell sx={{ color: "#7e7777b5", textAlign: "center" }}> Ativo</TableCell>
				</TableRow>
				<TableBody>
					{Isloading ? (
						<TableRow>
							<TableCell sx={{ textAlign: "center" }} colSpan={4}>
								<CircularProgress />
							</TableCell>
						</TableRow>
					) : !partners?.length ? (
						<TableRow>
							<TableCell sx={{ textAlign: "center" }} colSpan={4}>
								Nenhum parceiro encontrado!
							</TableCell>
						</TableRow>
					) : (
						partners?.map((partner, index) => (
							<TableRow key={index} hover>
								<TableCell sx={{ fontWeight: "normal" }}>
									<Typography variant="subtitle2" color={"#959595"} fontWeight={"normal"}>
										{partner.name}
									</Typography>
								</TableCell>
								<TableCell sx={{ fontWeight: "normal" }}>
									<Typography variant="subtitle2" fontWeight={"normal"}>
										{partner.email}
									</Typography>
								</TableCell>
								<TableCell sx={{ fontWeight: "normal" }}>
									{(() => {
										const derivedStatus = normalizeStatus((partner as any).status);
										return <Chip size="medium" label={derivedStatus === "inactive" ? "Inativo" : "Ativo"} sx={statusChipSx(derivedStatus)} />;
									})()}
								</TableCell>
								<TableCell sx={{ fontWeight: "normal", textAlign: "center" }}>
									<Switch
										checked={normalizeStatus((partner as any).status) === "active"}
										disabled={Isloading || isUpdatingStatus || !canActivatePartners}
										onChange={() => handleChangeActive(partner)}
										inputProps={{ "aria-label": "Ativar/Desativar parceiro" }}
									/>
								</TableCell>
							</TableRow>
						))
					)}
				</TableBody>
			</Table>

			<Dialog
				open={isConfirmDialogOpen}
				onClose={() => {
					setIsConfirmDialogOpen(false);
					setPendingPartner(null);
				}}
				disableEscapeKeyDown={isUpdatingStatus}
			>
				<DialogTitle>Atenção</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Ao ativar este parceiro, os demais parceiros serão desativados automaticamente. Deseja continuar?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button
						disabled={isUpdatingStatus}
						onClick={() => {
							setIsConfirmDialogOpen(false);
							setPendingPartner(null);
						}}
					>
						Cancelar
					</Button>
					<Button
						variant="contained"
						disabled={isUpdatingStatus || !pendingPartner || !canActivatePartners}
						onClick={async () => {
							if (!pendingPartner) return;
							setIsUpdatingStatus(true);
							try {
								await deactivateOtherPartners(pendingPartner);
								await setPartnerStatus(pendingPartner, "active");
								setIsConfirmDialogOpen(false);
								setPendingPartner(null);
							} catch {
								// Erro já tratado com toast
							} finally {
								setIsUpdatingStatus(false);
							}
						}}
					>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>
		</TableContainer>
	);
};
